import { useEffect, useRef, useState } from 'react';
import { getEmbedUrl } from '../utils/media';

// Owns the SOP / document viewer: the `viewingDocument` entry point
// plus every piece of PDF-iframe state (url, title, filename, zoom,
// filter, final url with #view hash, the Chromium error counter), the
// acknowledgement-panel inputs (isSigning, ackName), the three
// hide-toggle booleans, and the two resizable section percentages
// (desc / pdf — video gets the remainder).
//
// Effects owned:
//   * Reset-hide-defaults when the viewed document changes (video-only
//     docs flip to video-open; everything else defaults to pdf-open).
//   * Global error listener for the Chromium "StreamContainer" / tabId
//     / runtime.lastError noise that intermittently kills the PDF
//     embed — we bump `pdfErrorCount` which the viewer uses to force a
//     re-mount of the iframe.
//   * URL loader: whenever `viewingDocument` changes, derive
//     pdfViewerUrl / title / filename from whichever of
//     (videoUrl+embed, fileUrl, Electron filename) is present and fall
//     back to clearing everything if nothing usable is attached.
//   * Final URL builder: base64-wraps data-URL PDFs in an HTML shell
//     (so we can pass the #view=FitH toolbar flag through embed) and
//     appends the #view hash to plain http URLs.
//
// Acknowledge / save-signature / delete-acknowledgement handlers stay
// in the parent for now because they mutate `documents` and call
// `handleSave(getAllData())` — they'll move into `useAppData` in 6f.
// The parent reaches in via the exposed `setViewingDocument` /
// `setAckName` / `setIsSigning` setters when those handlers fire.
export function usePdfViewer() {
  const [viewingDocument, setViewingDocument] = useState(null);

  const [pdfViewerUrl, setPdfViewerUrl] = useState(null);
  const [pdfViewerTitle, setPdfViewerTitle] = useState('');
  const [pdfViewerFilename, setPdfViewerFilename] = useState('');
  const [pdfZoomLevel, setPdfZoomLevel] = useState(100);
  const [pdfFinalUrl, setPdfFinalUrl] = useState('');
  const [pdfErrorCount, setPdfErrorCount] = useState(0);
  const [isPdfFilterEnabled, setIsPdfFilterEnabled] = useState(true);

  const [isSigning, setIsSigning] = useState(false);
  const [ackName, setAckName] = useState('');

  // Viewer-tab value itself is never read anywhere — only the setter is
  // passed to MainPanel so the URL-loader effect below can switch it
  // alongside the file-url resolution. Kept as setter-only to match
  // the pre-refactor surface exactly.
  const [, setActiveViewerTab] = useState('document');

  const [hideDescription, setHideDescription] = useState(false);
  const [hideDocument, setHideDocument] = useState(false);
  const [hideVideo, setHideVideo] = useState(true);

  const [descPercent, setDescPercent] = useState(10);
  const [pdfPercent, setPdfPercent] = useState(40);

  const viewerRef = useRef(null);
  const dragInfoRef = useRef(null);

  // Video-only docs default to video expanded; everything else opens
  // on the document pane. Runs on every viewingDocument change.
  useEffect(() => {
    if (viewingDocument && !viewingDocument.filename && viewingDocument.videoUrl) {
      setHideVideo(false);
      setHideDocument(true);
    } else {
      setHideVideo(true);
      setHideDocument(false);
    }
  }, [viewingDocument]);

  // Chromium occasionally fires "Failed to get StreamContainer" /
  // "Cannot read properties of undefined (reading 'tabId')" /
  // runtime.lastError from inside the PDF plugin. Bumping
  // pdfErrorCount forces the viewer's iframe to re-mount.
  useEffect(() => {
    const handleError = (event) => {
      const msg = (event.message || (event.reason && event.reason.message) || '').toString();
      if (
        msg.includes('StreamContainer') ||
        msg.includes('tabId') ||
        msg.includes('runtime.lastError')
      ) {
        console.warn('Caught Chromium PDF error, refreshing viewer...', msg);
        setPdfErrorCount((c) => c + 1);
      }
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  // Resolve pdf url / title / filename / active tab from whichever
  // source the document provides. Also reset the error counter so the
  // viewer iframe remounts fresh on each open, and auto-close the
  // signing panel if the new doc doesn't require a signature.
  useEffect(() => {
    if (viewingDocument) {
      setPdfErrorCount(0);

      const isEmbed = viewingDocument.videoUrl && getEmbedUrl(viewingDocument.videoUrl);
      const isVideoFile = (viewingDocument.fileType && viewingDocument.fileType.startsWith('video')) || (viewingDocument.fileUrl && /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(viewingDocument.fileUrl)) || (viewingDocument.videoUrl && /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(viewingDocument.videoUrl));

      if (isEmbed) {
        setPdfViewerUrl('');
        setPdfViewerTitle(viewingDocument.name);
        setPdfViewerFilename('');
        setActiveViewerTab('video');
      } else if (viewingDocument.fileUrl) {
        setPdfViewerUrl(viewingDocument.fileUrl);
        setPdfViewerTitle(viewingDocument.name);
        setPdfViewerFilename(viewingDocument.fileUrl);
        setActiveViewerTab(isVideoFile ? 'document' : 'document');
      } else if (viewingDocument.filename && window.electronAPI) {
        const getUrl = async () => {
          const fileUrl = await window.electronAPI.getDocumentPath(viewingDocument.filename);
          if (fileUrl) {
            setPdfViewerUrl(fileUrl);
            setPdfViewerTitle(viewingDocument.name);
            setPdfViewerFilename(viewingDocument.filename);
            const fileIsVideo = /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(fileUrl);
            setActiveViewerTab(fileIsVideo ? 'document' : 'document');
          } else {
            console.error('Failed to get document path');
            setPdfViewerUrl(null);
          }
        };
        getUrl();
      } else {
        setPdfViewerUrl(null);
        setPdfViewerTitle('');
        setPdfViewerFilename('');
        setActiveViewerTab('document');
      }

      if (!viewingDocument.signatureRequired) setIsSigning(false);
    } else {
      setPdfViewerUrl(null);
      setPdfViewerTitle('');
      setPdfViewerFilename('');
      setIsSigning(false);
      setActiveViewerTab('document');
    }
  }, [viewingDocument]);

  // data: URLs can't take a #view hash, so wrap them in an HTML shell
  // that hosts the embed + hash. Plain http URLs just get the hash
  // appended and flow straight through to the iframe.
  useEffect(() => {
    if (pdfViewerUrl && pdfViewerUrl.startsWith('data:')) {
      const htmlContent = `
        <!DOCTYPE html>
        <html style="height: 100%;">
          <head><title>${pdfViewerTitle}</title></head>
          <body style="margin:0; padding:0; overflow:hidden; background-color: #FFFFFF; height: 100%;">
            <embed src="${pdfViewerUrl}#view=FitH&pagelayout=singlepage&toolbar=0&zoom=${pdfZoomLevel}" type="application/pdf" width="100%" height="100%" style="${isPdfFilterEnabled ? 'filter: brightness(95%) contrast(95%);' : ''}" />
          </body>
        </html>
      `;
      const htmlDataUrl = `data:text/html;base64,${btoa(htmlContent)}`;
      setPdfFinalUrl(htmlDataUrl);
    } else if (pdfViewerUrl) {
      setPdfFinalUrl(`${pdfViewerUrl}#view=FitH&pagelayout=singlepage&toolbar=0&zoom=${pdfZoomLevel}`);
    } else {
      setPdfFinalUrl('');
    }
  }, [pdfViewerUrl, pdfZoomLevel, pdfViewerTitle, isPdfFilterEnabled]);

  // Two drag handles in DocumentViewer: desc↔pdf and pdf↔video. Desc
  // clamps 5..85%, pdf clamps so desc+pdf stays ≤90% (leaving ≥10%
  // for video). We stash a snapshot in dragInfoRef on mousedown so
  // dragging is relative to the starting sizes rather than stacking.
  const handleDragging = (e) => {
    if (!dragInfoRef.current) return;
    const info = dragInfoRef.current;
    const deltaPx = e.clientY - info.startY;
    const deltaPercent = (deltaPx / info.containerHeight) * 100;
    if (info.type === 'descPdf') {
      let newDesc = info.startDesc + deltaPercent;
      let newPdf = info.startPdf - deltaPercent;
      newDesc = Math.max(5, Math.min(85, newDesc));
      newPdf = Math.max(5, Math.min(90 - newDesc, newPdf));
      setDescPercent(newDesc);
      setPdfPercent(newPdf);
    } else if (info.type === 'pdfVideo') {
      let newPdf = info.startPdf + deltaPercent;
      const maxPdf = 95 - info.startDesc;
      newPdf = Math.max(5, Math.min(maxPdf, newPdf));
      setPdfPercent(newPdf);
    }
  };

  const stopDrag = () => {
    document.removeEventListener('mousemove', handleDragging);
    document.removeEventListener('mouseup', stopDrag);
    dragInfoRef.current = null;
  };

  const startDrag = (type, e) => {
    e.preventDefault();
    if (!viewerRef.current) return;
    dragInfoRef.current = {
      type,
      startY: e.clientY,
      startDesc: descPercent,
      startPdf: pdfPercent,
      containerHeight: viewerRef.current.clientHeight || window.innerHeight * 0.9,
    };
    document.addEventListener('mousemove', handleDragging);
    document.addEventListener('mouseup', stopDrag);
  };

  const handlePrintPdf = () => {
    if (pdfViewerFilename && window.electronAPI) {
      window.electronAPI.printDocument(pdfViewerFilename);
    } else if (pdfFinalUrl) {
      const w = window.open(pdfFinalUrl, '_blank');
      if (w) {
        w.focus();
        setTimeout(() => { try { w.print(); } catch (e) { console.error('Print failed', e); } }, 600);
      }
    } else if (pdfViewerFilename && pdfViewerFilename.startsWith('http')) {
      window.open(pdfViewerFilename, '_blank');
    }
  };

  const handleDownloadPdf = () => {
    if (pdfViewerFilename && window.electronAPI) {
      window.electronAPI.downloadDocument(pdfViewerFilename);
    } else if (pdfViewerFilename && pdfViewerFilename.startsWith('http')) {
      window.open(pdfViewerFilename, '_blank');
    }
  };

  const closeViewer = () => {
    setViewingDocument(null);
    setPdfViewerUrl('');
    setPdfFinalUrl('');
  };

  return {
    viewingDocument, setViewingDocument,
    pdfZoomLevel, setPdfZoomLevel,
    pdfFinalUrl,
    pdfErrorCount,
    isPdfFilterEnabled, setIsPdfFilterEnabled,
    isSigning, setIsSigning,
    ackName, setAckName,
    setActiveViewerTab,
    hideDescription, setHideDescription,
    hideDocument, setHideDocument,
    hideVideo, setHideVideo,
    descPercent,
    pdfPercent,
    viewerRef,
    startDrag,
    handlePrintPdf,
    handleDownloadPdf,
    closeViewer,
  };
}
